/** Shared version + last-updated provenance for Configuration / assumptions panels. */

import type { ReactNode } from 'react';

export interface ConfigMetaBadgeProps {
  version?: string | null;
  updatedAt?: string | null;
  /** Cognito login email (or other actor id) from config `updated_by`. */
  updatedBy?: string | null;
  /** Prefix before the formatted time. Defaults to "Last updated". */
  updatedPrefix?: string;
  className?: string;
  title?: string;
  /**
   * version — compact header chip (vX.Y only)
   * provenance — full-width “Last updated … · by email” line
   * legacy — version + updated time inline (old header layout)
   */
  variant?: 'version' | 'provenance' | 'legacy';
}

function formatLastUpdated(iso: string): string {
  try {
    // en-GB → DD/MM/YYYY (day first); avoid browser default US MM/DD.
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
}

/** Hide placeholder / transport actors that are not a real login identity. */
export function formatConfigUpdatedBy(raw?: string | null): string | null {
  const s = String(raw ?? '').trim();
  if (!s) return null;
  const lower = s.toLowerCase();
  if (
    lower === 'user'
    || lower === 'system'
    || lower === 'system_defaults'
    || lower === 'api'
    || lower === 'worker'
    || lower === 'fleet_cost_standing_sync'
  ) {
    return null;
  }
  return s;
}

/** True when this save restored built-in defaults (admin Reset → Save). */
export function isConfigRestoredDefaultsActor(raw?: string | null): boolean {
  const lower = String(raw ?? '').trim().toLowerCase();
  return lower === 'system_defaults';
}

export default function ConfigMetaBadge({
  version,
  updatedAt,
  updatedBy,
  updatedPrefix = 'Last updated',
  className = '',
  title,
  variant = 'legacy',
}: ConfigMetaBadgeProps) {
  const versionLabel = String(version || '').trim();
  const updatedLabel = updatedAt ? formatLastUpdated(updatedAt) : '';
  const byLabel = formatConfigUpdatedBy(updatedBy);

  if (variant === 'version') {
    if (!versionLabel) return null;
    return (
      <span
        className={`text-sm font-medium text-foreground whitespace-nowrap ${className}`}
        title={title ?? 'Config version'}
      >
        v{versionLabel.replace(/^v/i, '')}
      </span>
    );
  }

  if (variant === 'provenance') {
    if (!updatedLabel && !byLabel) return null;
    const parts: ReactNode[] = [];
    if (updatedLabel) {
      parts.push(
        <span key="when" className="text-foreground">
          {updatedPrefix} {updatedLabel}
        </span>,
      );
    }
    if (byLabel) {
      parts.push(
        <span key="who" className="text-chart-10/90">
          by {byLabel}
        </span>,
      );
    }
    return (
      <p
        className={`text-xs min-w-0 truncate ${className}`}
        title={title ?? [updatedLabel && `${updatedPrefix} ${updatedLabel}`, byLabel && `by ${byLabel}`].filter(Boolean).join(' · ')}
        role="status"
      >
        {parts.reduce<ReactNode[]>((acc, part, i) => {
          if (i > 0) acc.push(<span key={`sep-${i}`} className="text-muted-foreground"> · </span>);
          acc.push(part);
          return acc;
        }, [])}
      </p>
    );
  }

  // legacy — version + updated time (no by-line; prefer provenance variant for authorship)
  if (!versionLabel && !updatedLabel) return null;

  return (
    <div
      className={`flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-3 min-w-0 ${className}`}
      title={title ?? 'Last loaded or saved metadata'}
    >
      {versionLabel ? (
        <span className="text-sm font-medium text-foreground whitespace-nowrap">
          v{versionLabel.replace(/^v/i, '')}
        </span>
      ) : null}
      {updatedLabel ? (
        <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">
          {updatedPrefix} {updatedLabel}
          {byLabel ? ` · by ${byLabel}` : ''}
        </span>
      ) : byLabel ? (
        <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">by {byLabel}</span>
      ) : null}
    </div>
  );
}
